import { App, Divider, Form, Upload } from 'antd';
import { useIntl } from 'react-intl';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useOutletContext, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import PropTypes from 'prop-types';
import RegionSelect from '../../../../../../components/RegionSelect/RegionSelect';
import { MainContext } from '../../../../../../context/MainContext';
import {
  createFileOnBackend,
  getFile,
} from '../../../../../../services/api/files';
import fileIcon from '../../../../../../style/img/file.png';
import { NotificationsContext } from '../../../../../../context/NotificationsContext';

import styles from './index.module.scss';
import AccionaButton from '../../../../../../components/AccionaButton';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import { updateDeliveryNotesFileExtensionsButton } from '../../../../../../constants/general';

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.extend(customParseFormat);

const TemplateLayout = ({
  initialData,
  onBack,
  onFinish,
  header,
  topFormFields,
  model,
  regions: _regions,
}) => {
  const intl = useIntl();
  const { notification } = App.useApp();
  const [form] = Form.useForm();
  const { project } = useOutletContext();
  const { setLoading } = useContext(MainContext);
  const { pushNotification } = useContext(NotificationsContext);
  const { fileName, validated } = useParams();
  const isXlsxFile = fileName?.includes('xlsx');
  const [selectedFile, setSelectedFile] = useState();
  const [regions, setRegions] = useState(_regions);
  const [selectedRegion, setSelectedRegion] = useState();
  const [moveAllRegions, setMoveAllRegions] = useState(false);
  const [unLockedRegion, setUnLockedRegion] = useState('');
  const [currentFileName, setCurrentFileName] = useState(fileName);
  const regionsRef = useRef(regions);

  const isANoValidatedAndXlsxDeliveryNote =
    validated === 'noValidated' && fileName.includes('xlsx');

  const validateInitialData = useCallback(
    async (formData) => {
      if (!formData || Object.keys(formData).length === 0) return false;

      const notVisibleCount = regionsRef.current.filter(
        (r) => r.isVisible === false,
      ).length;

      const showValidationNotification = (errorCount) => {
        let description = '';
        if (errorCount > 0) {
          description = intl.formatMessage(
            { id: 'validations.initialCheck.errors.description' },
            { count: errorCount },
          );
        }
        if (notVisibleCount > 0) {
          const regionsMsg = intl.formatMessage(
            { id: 'validations.initialCheck.regions.description' },
            { notVisibleCount },
          );
          description += (description ? ' ' : '') + regionsMsg;
        }
        if (description) {
          notification.warning({
            message: intl.formatMessage({
              id: 'validations.initialCheck.errors.title',
            }),
            description,
            duration: 6,
            placement: 'topRight',
          });
        }
      };

      return form
        .validateFields()
        .then(() => {
          if (notVisibleCount > 0) {
            showValidationNotification(0);
            return false;
          }
          return true;
        })
        .catch(() => {
          showValidationNotification(
            form.getFieldsError().filter(({ errors }) => errors.length).length,
          );
          return false;
        });
    },
    [form, intl, notification],
  );

  const updateMoveAllRegions = () => {
    if (moveAllRegions) {
      setMoveAllRegions(false);
    }
  };

  const onClickLock = (data, region) => {
    if (!unLockedRegion) {
      setUnLockedRegion(data);
      setSelectedRegion(data);
      if (region.isVisible === false) {
        setRegions((prevRegions) => {
          return prevRegions.map((reg) =>
            reg.data === data
              ? {
                  ...reg,
                  isVisible: true,
                  x: 0,
                  y: 0,
                  width: 10,
                  height: 4,
                }
              : reg,
          );
        });
      }
    }

    if (unLockedRegion === data) {
      setUnLockedRegion();
      setSelectedRegion();
    }
  };

  useEffect(() => {
    regionsRef.current = regions;
  }, [regions]);

  useEffect(() => {
    async function loadFile(name) {
      setLoading(true);
      try {
        const file = await getFile(name.replace('.pdf', '.png'), model);
        setSelectedFile(URL.createObjectURL(file.data));
      } catch (error) {
        if (error.response.status === 404) {
          setSelectedFile(fileIcon);
        } else {
          pushNotification({
            message: intl.formatMessage({ id: 'file.loadError' }),
            type: 'error',
            callback: null,
          });
        }
      } finally {
        setLoading(false);
      }
    }
    loadFile(fileName);
    form.setFieldsValue(initialData);
    validateInitialData(initialData);
  }, [
    fileName,
    setLoading,
    initialData,
    model,
    pushNotification,
    intl,
    validateInitialData,
    form,
  ]);

  useEffect(() => {
    setRegions(_regions);
  }, [_regions]);

  return (
    <div className="container" style={{ backgroundColor: 'white' }}>
      <div className="row">
        <div className="col-xs-12">
          <div style={{ padding: '2em' }}>
            {header}

            <Form
              layout="vertical"
              form={form}
              onFinish={() => {
                const rawData = form.getFieldsValue(true);
                onFinish(regions, { ...rawData }, currentFileName);
              }}
            >
              <div className="row" style={{ marginBottom: '1.5em' }}>
                {topFormFields}
              </div>

              <div className="row gap-1">
                <div className="col-xs-12 col-md-9">
                  <div style={{ display: 'flex', marginBottom: '2em' }}>
                    {!isXlsxFile && regions?.length && (
                      <AccionaButton
                        msg={intl.formatMessage({
                          id: 'pages.quality.ocr.templates.create.delivery.templateLayout.moveAllRegions',
                        })}
                        type={moveAllRegions ? 'primary' : 'default'}
                        style={{
                          height: 35,
                          fontSize: '0.9em',
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          padding: '0px 10px',
                        }}
                        onClick={() => {
                          setMoveAllRegions((prev) => !prev);
                        }}
                        classIcon={`fas fa-${
                          !moveAllRegions ? 'lock' : 'unlock-alt'
                        }`}
                      />
                    )}
                    {isANoValidatedAndXlsxDeliveryNote && (
                      <div className="row" style={{ marginBottom: '1.5em' }}>
                        <div className="col-xs-12 col-md-6">
                          <Upload
                            maxCount={1}
                            accept={updateDeliveryNotesFileExtensionsButton}
                            customRequest={({ file, onError, onSuccess }) => {
                              createFileOnBackend(
                                file,
                                'delivery',
                                project.id_project,
                                false,
                              )
                                .then(({ data }) => {
                                  onSuccess(data, file);
                                  if (data) {
                                    getFile(data.fileName, model).then(
                                      ({ data: dataFile }) => {
                                        setSelectedFile(
                                          URL.createObjectURL(dataFile),
                                        );
                                        setCurrentFileName(data.fileName);
                                      },
                                    );
                                  } else {
                                    setSelectedFile(fileIcon);
                                    setCurrentFileName(fileName);
                                  }
                                })
                                .catch(onError);
                            }}
                            onRemove={() => {
                              setSelectedFile(fileIcon);
                              setCurrentFileName(fileName);
                            }}
                          >
                            <AccionaButton
                              msg={intl.formatMessage({
                                id: 'uploadDeliveryNote',
                              })}
                              type="primary"
                              classIcon="fas fa-file-upload"
                              style={{
                                height: 35,
                                fontSize: '0.9em',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                padding: '0px 10px',
                              }}
                            />
                          </Upload>
                        </div>
                      </div>
                    )}{' '}
                  </div>
                  <RegionSelect
                    regions={regions}
                    constraint
                    onChange={setRegions}
                    selectedRegion={selectedRegion}
                    setSelectedRegion={setSelectedRegion}
                    moveAllRegions={moveAllRegions}
                    unLockedRegion={unLockedRegion}
                    onClickLock={onClickLock}
                    updateMoveAllRegions={updateMoveAllRegions}
                    style={{
                      border: '1px solid #F1F1F1',
                      ...(fileName === 'xlsx.png'
                        ? {
                            maxWidth: '100%',
                            minHeight: '1000px',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                          }
                        : {}),
                    }}
                    disableLocks={isXlsxFile}
                  >
                    <img
                      alt="region-select-img"
                      style={{ userSelect: 'none' }}
                      id="region-select-canvas"
                      src={selectedFile}
                    />
                  </RegionSelect>
                </div>
                <div className="col-xs-12 col-md-3">
                  <div
                    style={{
                      border: '1px solid #ececec',
                      background: '#ececec',
                    }}
                  >
                    <h4 className={styles.fieldGroupsTitle}>
                      {intl.formatMessage({
                        id: 'pages.quality.ocr.templates.create.delivery.templateLayout.fields.title',
                      })}
                    </h4>
                    <div
                      style={{ backgroundColor: '#fff', padding: '1em 0.5em' }}
                    >
                      {regions.map((region) => {
                        const { data, isVisible } = region;
                        return (
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'flex-start',
                              gap: 10,
                              padding: 10,
                              fontSize: 15,
                            }}
                          >
                            <span>
                              <i
                                className={`icon fas fa-${
                                  unLockedRegion !== data || !isVisible
                                    ? 'lock'
                                    : 'unlock-alt'
                                } ${
                                  !isVisible
                                    ? 'iconGray'
                                    : unLockedRegion !== data
                                    ? 'iconGreen'
                                    : 'iconRed'
                                } fa-lg`}
                                onClick={() => onClickLock(data, region)}
                                style={{ cursor: 'pointer' }}
                              />
                            </span>
                            {intl.formatMessage({
                              id: `pages.quality.ocr.templates.create.delivery.templateLayout.field.${data}`,
                            })}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
              <div className="row">
                <div className="col-xs-12">
                  <Divider />
                </div>
              </div>
              <div className="row">
                <div className="col-xs-12">
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                    }}
                  >
                    <AccionaButton
                      msg={intl.formatMessage({ id: 'back' })}
                      onClick={onBack}
                      classIcon="icon fas fa-chevron-left"
                      style={{ float: 'right', marginRight: '-20px' }}
                    />
                    <AccionaButton
                      id="saveTemplateBtn"
                      onClick={async () => {
                        const formData = form.getFieldsValue(true);
                        const isValid = await validateInitialData(formData);
                        if (isValid) form.submit();
                      }}
                      msg={intl.formatMessage({ id: 'save' })}
                      classIcon="icon fas fa-check"
                    />
                  </div>
                </div>
              </div>
            </Form>
          </div>
        </div>
      </div>
    </div>
  );
};

TemplateLayout.propTypes = {
  initialData: PropTypes.object,
  fieldGroups: PropTypes.shape({
    mandatory: PropTypes.arrayOf(PropTypes.string),
    other: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  formValues: PropTypes.object,
  validableFields: PropTypes.arrayOf(PropTypes.string).isRequired,
  onBack: PropTypes.func.isRequired,
  onFinish: PropTypes.func.isRequired,
  mode: PropTypes.oneOf(['create', 'edit']),
  header: PropTypes.node,
  topFormFields: PropTypes.node,
  model: PropTypes.oneOf(['delivery', 'deliveryPC', 'laboratory', 'template'])
    .isRequired,
  regions: PropTypes.array.isRequired,
};

TemplateLayout.defaultProps = {
  initialData: {},
  formValues: {},
  mode: 'create',
  header: undefined,
  topFormFields: undefined,
  model: 'delivery',
};

export default TemplateLayout;
